import { useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography } from '@mui/material';
import _ from 'lodash';

import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import {
  HookFormToggle,
  HookQuerySearchSelect,
} from 'ui-component/HookFormComponents';
import {
  useUpdateShipmentLineMutation,
  useGetStockLotsServerSideQuery,
} from 'store/slices/apiV1/inventory';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { StockLotQuerySchema, packageValueOptions } from 'types/inventory';
import useSnackbar from 'hooks/useSnackbar';
import { handleErr } from 'utils/functions';
import {
  formConstants,
  formValidation,
} from 'views/inventory/InventoryShipment/components/EditShipmentLineDialog/constants';

const EditShipmentLineDialog = ({
  shipmentId,
  lineId,
  dialogOpen,
  onClose,
  excludedStockLotIds,
}: {
  shipmentId: string;
  lineId: string;
  dialogOpen: boolean;
  onClose: () => void;
  excludedStockLotIds: string[];
}) => {
  const { dispatchSuccessSnackbar } = useSnackbar();
  const [error, setError] = useState<string | null>(null);
  const [updateShipmentLine, { isLoading: isUpdatingShipmentLine }] =
    useUpdateShipmentLineMutation();

  const formDefaultValues = {
    [formConstants.stockLot.name]: '',
    [formConstants.transferRequests.name]: false,
  };

  const {
    control,
    formState: { errors, touchedFields },
    setValue,
    handleSubmit,
    reset,
  } = useForm<FieldValues>({
    defaultValues: formDefaultValues,
    resolver: yupResolver(formValidation),
    mode: 'all',
  });

  const handleClose = () => {
    onClose();
    reset();
    setError(null);
  };

  const onSubmit = async (values: FieldValues) => {
    try {
      setError(null);
      await updateShipmentLine({
        shipmentId,
        shipmentLineId: lineId,
        payload: {
          stockLot: values.stockLot,
          transferRequests: values.transferRequests,
        },
      }).unwrap();
      dispatchSuccessSnackbar('Shipment line updated');
      handleClose();
    } catch (err) {
      handleErr(err, (errMessage: string) => setError(errMessage));
    }
  };

  return (
    <ExtendedDialog
      onCloseDialog={handleClose}
      open={dialogOpen}
      title="Change Stock Lot"
      isForm
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isUpdatingShipmentLine}
      submitButtonCopy="Save"
      formSubmitError={error}
      permissionScope={{
        app: ALL_APPS.KITTING.id,
        accessLevel: appPermissionAccessLevels.edit,
      }}
    >
      <Typography>
        Select a new stock lot to replace the current stock lot. Optionally,
        open handling tasks related to this shipment can be transferred to the
        new stock lot.
      </Typography>
      <HookQuerySearchSelect<StockLotQuerySchema>
        errors={errors}
        control={control}
        name={formConstants.stockLot.name}
        label={formConstants.stockLot.label}
        useGetQuery={useGetStockLotsServerSideQuery}
        filters={[
          {
            field: 'quant',
            operator: '>',
            value: 0,
          },
        ]}
        excludes={[
          {
            field: 'id',
            operator: 'isAnyOf',
            value: excludedStockLotIds,
          },
        ]}
        schema={['quant', 'lotId', 'package', { part: ['mpn', 'id'] }]}
        getOptionLabel={(option) =>
          `${option.part?.mpn}: ${option.quant}pc ${
            option.package
              ? ` -  ${
                  _.find(packageValueOptions, ['value', option.package])?.label
                }`
              : ''
          } (${option.part?.id}${option.lotId})`
        }
      />
      <HookFormToggle
        errors={errors}
        control={control}
        name={formConstants.transferRequests.name}
        label={formConstants.transferRequests.label}
      />
    </ExtendedDialog>
  );
};

export default EditShipmentLineDialog;
